import React, { useState } from 'react';
import SuccessModal from './SuccessModal';
import apiClient, { getToken } from '../auth/service';
import { User } from '../types';

interface ChangePasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUser: User | null;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose, currentUser }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [passwordCriteria, setPasswordCriteria] = useState({
        minLength: false,
        uppercase: false,
        lowercase: false,
        specialChar: false,
    });

    const validatePassword = (password: string) => {
        setPasswordCriteria({
            minLength: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        });
    };

    const allCriteriaMet = Object.values(passwordCriteria).every(Boolean);

    const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setNewPassword(value);
        validatePassword(value);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (newPassword !== confirmPassword) {
            setError('As novas senhas não coincidem.');
            return;
        }
        if (!allCriteriaMet) {
            setError('A nova senha não atende a todos os critérios de segurança.');
            return;
        }

        if (!currentUser?._id) {
            setError('Não foi possível identificar o usuário. Por favor, faça login novamente.');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        // Adiciona log para depuração: verifica se o token está presente
        console.log('Attempting to change password. Checking token...');
        const currentToken = getToken();
        if (!currentToken) {
            console.warn('No authentication token found for password change request.');
            setError('Sessão expirada ou token de autentica\xE7\xE3o ausente. Por favor, fa\xE7a login novamente.');
            setIsLoading(false);
            return;
        }
        console.log('Authentication token found:', currentToken ? 'present' : 'absent');

        try {
            if (currentUser && currentUser._id) {
                await apiClient.put(`/user/${currentUser._id}/password`, { currentPassword, newPassword });
            } else {
                throw new Error("Usuário não autenticado.");
            }
            // Fechar modal principal e mostrar mensagem de sucesso
            console.log('Password changed successfully, closing main modal...');
            onClose();
            console.log('Main modal closed, showing success modal...');
            setTimeout(() => {
                console.log('Opening success modal...');
                setShowSuccessModal(true);
            }, 300);
        } catch (err: any) {
            setError(err.message || 'Ocorreu um erro desconhecido ao alterar a senha.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseSuccess = () => {
        setShowSuccessModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        onClose();
    };

    // Mantemos o componente montado para que o modal de sucesso possa aparecer

    const renderPasswordCriteria = () => {
        const criteria = [
            { met: passwordCriteria.minLength, text: 'Pelo menos 8 caracteres' },
            { met: passwordCriteria.uppercase, text: 'Uma letra maiúscula' },
            { met: passwordCriteria.lowercase, text: 'Uma letra minúscula' },
            { met: passwordCriteria.specialChar, text: 'Um caractere especial' },
        ];
        return (
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-left mt-2">
                {criteria.map(c => (
                     <div key={c.text} className={`transition-colors ${c.met ? 'text-green-400' : 'text-slate-400'}`}>
                        <i className={`fas ${c.met ? 'fa-check-circle' : 'fa-times-circle'} mr-2`}></i>
                        {c.text}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <>
            <SuccessModal isOpen={showSuccessModal} title="Senha Alterada" onConfirm={handleCloseSuccess} buttonText="Ok">
                Senha Alterada
            </SuccessModal>
            {isOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex justify-center items-center p-4" aria-modal="true" role="dialog">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl p-6 w-full max-w-md text-left animate-fade-in">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Alterar Senha</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="currentPassword"className="block text-sm font-medium text-gray-700 dark:text-slate-300">Senha Atual</label>
                            <input type="password" name="currentPassword" id="currentPassword" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm"/>
                        </div>
                        <div>
                            <label htmlFor="newPassword"className="block text-sm font-medium text-gray-700 dark:text-slate-300">Nova Senha</label>
                            <input type="password" name="newPassword" id="newPassword" value={newPassword} onChange={handleNewPasswordChange} required className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm"/>
                            {renderPasswordCriteria()}
                        </div>
                        <div>
                            <label htmlFor="confirmPassword"className="block text-sm font-medium text-gray-700 dark:text-slate-300">Confirmar Nova Senha</label>
                            <input type="password" name="confirmPassword" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="mt-1 block w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm"/>
                        </div>
                        {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                        <div className="flex justify-end gap-4 pt-4">
                            <button type="button" onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-slate-600 dark:hover:bg-slate-500 dark:text-white font-bold py-2 px-6 rounded-lg">Cancelar</button>
                            <button type="submit" disabled={isLoading || !allCriteriaMet} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-slate-600"> {isLoading ? 'Salvando...' : 'Salvar'}</button>
                        </div>
                    </form>
                </div>
            </div>
            )}
        </>
    );
};

export default ChangePasswordModal;